// components/AdvisoriesDetails.jsx
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faShieldAlt,
  faVirusSlash,
  faBug,
  faRadiation,
  faCalendarAlt,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import PageHeader from "../../componentes/PageHeader";
import GradientBanner from "../../componentes/Banner";
import LoadingSkeleton from "../../componentes/LoadingSkeleton";
import { FullscreenSpinner } from "../../componentes/LoadingIndicator";
import { fetchAdvisoryDetails } from "../../api/advisories";
import { formatDate } from "../../utils/dateFormatter";
import "./ckeditor-content.css";
import "../../styles/circuit-background.css";

const defaultImage = "https://c.animaapp.com/7PaZ1XR8/img/download-2-2@2x.png";
const backgroundIcons = [
  faExclamationTriangle,
  faShieldAlt,
  faVirusSlash,
  faBug,
  faRadiation,
];

const AdvisoriesDetails = () => {
  const { slug } = useParams(); 
  const { t, i18n } = useTranslation();
  const lang = useSelector((state) => state.language.selectedLanguage);


  const {
    data: advisory,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["advisoryDetails", slug, lang],
    queryFn: () => fetchAdvisoryDetails(slug, lang),
  });

  if (isLoading) {
    return (
      <>
        <PageHeader />
        <GradientBanner
          title={t("advisories.loading")}
          description=""
          path={[
            { label: t("header.home"), link: "/" },
            { label: t("advisories.title"), link: "/advisories" },
            { label: t("advisories.loading"), link: null },
          ]}
        />
        <FullscreenSpinner />
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader />
        <GradientBanner
          title={t("advisories.error")}
          description={
            t("advisories.error_description") || "Something went wrong"
          }
          path={[
            { label: t("header.home"), link: "/" },
            { label: t("advisories.title"), link: "/advisories" },
            { label: t("advisories.error"), link: null },
          ]}
        />
        <div className="flex justify-center items-center min-h-[400px]">
          <p className="text-red-500">{error.message}</p>
        </div>
      </>
    );
  }

  if (!advisory) {
    return (
      <>
        <PageHeader />
        <GradientBanner
          title={t("advisories.not_found")}
          description={
            t("advisories.not_found_description") || "Advisory not found"
          }
          path={[
            { label: t("header.home"), link: "/" },
            { label: t("advisories.title"), link: "/advisories" },
            { label: t("advisories.not_found"), link: null },
          ]}
        />
        <div className="flex justify-center items-center min-h-[400px]">
          <p className="text-gray-500">
            {t("advisories.not_found_message") ||
              "The requested advisory could not be found."}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader />
      <div className="max-xl:pl-4 bg-gradient-to-r from-midnigt to-shake mt-[72px] pt-8 sm:pt-8 xl:pt-8 px-dynamic min-h-[14rem] md:min-h-[15rem] relative overflow-hidden">
        {backgroundIcons.length > 0 && (
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-float"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  opacity: 0.1,
                }}
              >
                <FontAwesomeIcon
                  icon={backgroundIcons[i % backgroundIcons.length]}
                  className="text-white text-4xl"
                />
              </div>
            ))}
          </div>
        )}

        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-2 text-white/80 text-sm md:mb-8 mb-6 max-xl:mt-8"
          >
            <Link to="/" className="hover:text-white transition-colors">
              {t("header.home")}
            </Link>
            <span>/</span>
            <Link
              to="/advisories"
              className="hover:text-white transition-colors"
            >
              {t("advisories.title")}
            </Link>
            <span>/</span>
            <span className="break-words">
              {advisory.title || t("advisories.title")}
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <motion.h1
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-h-5 font-bold text-white tracking-tight font-catamaran break-words max-w-[90%] md:max-w-[80%] mb-4 md:mb-6"
            >
              {advisory.title}
            </motion.h1>
          </motion.div>
        </div>
      </div>

      <div className="min-h-screen bg-gray-50">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-8">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="xl:col-span-2">
              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden"
              >
                {/* Article Header */}
                <div className="p-8 pb-6">
                  <div className="flex items-center gap-4 mb-6">
                    <span className="flex items-center text-sm text-gray-500 font-nunito-sans">
                      <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-2 text-primary" />
                      {formatDate(advisory.created_at, lang)}
                    </span>
                  </div>
                  
                </div>

                {/* Image Section */}
                {advisory.image && (
                  <div className="px-6 lg:px-8 mb-8">
                    <div className="aspect-video overflow-hidden rounded-xl shadow-lg">
                      <img
                        src={advisory.image}
                        alt={advisory.title || "Advisory image"}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = defaultImage;
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Article Content */}
                <div className="px-6 lg:px-8 pb-8">
                  {/* Language fallback notice */}
                  {advisory.is_fallback && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3"
                    >
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="text-blue-500 mt-1 flex-shrink-0"
                      />
                      <p className="text-blue-800 text-sm font-nunito-sans">
                        {t("advisories.language_fallback_notice", {
                          requested: i18n.language,
                          actual: advisory.actual_language,
                        })}
                      </p>
                    </motion.div>
                  )}

                  <div
                    dangerouslySetInnerHTML={{
                      __html:
                        advisory.description ||
                        t("advisories.no_description") ||
                        "No Description",
                    }}
                    className="rich-content"
                  />
                </div>
              </motion.article>
            </div>

            {/* Sidebar */}
            <div className="xl:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white rounded-2xl shadow-lg p-6 sticky top-8"
              >


                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FontAwesomeIcon icon={faCalendarAlt} className="text-primary w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="font-nunito-sans font-semibold text-gray-900 mb-1">
                        {t("advisories.first_published")}
                      </h4>
                      <p className="font-nunito-sans text-sm text-gray-600">
                        {formatDate(advisory.created_at, lang)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FontAwesomeIcon icon={faCalendarAlt} className="text-primary w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="font-nunito-sans font-semibold text-gray-900 mb-1">
                        {t("advisories.last_updated")}
                      </h4>
                      <p className="font-nunito-sans text-sm text-gray-600">
                        {formatDate(advisory.updated_at || advisory.created_at, lang)}
                      </p>
                    </div>
                  </div>
                </div>


              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdvisoriesDetails;
